/* ============================================================================
   Merchant Setup → Zone Mapping → Base Zone Upload   (route /merchant-config/zones/base-upload)

   Uploads the merchant BASE zone map — the PROZO_Z_MAP rows in Mongo
   zones_map_1..9 that merchant pricing reads for every merchant (before any
   per-merchant override). Same API as the legacy Courier Setup upload card
   (courier/setup/<id>?type=Zone Mapping → RateCard):
     POST tools/upload/servicability   multipart {file, type=BULK_SLA, prozoId, alt=true}
   Differences from the legacy card, on purpose:
     • prozoId is the backend constant PROZO_Z_MAP — the legacy page derived it
       from a courier record (PROZOZMAP) that does not exist in staging or prod.
     • alt=true selects uploadZoneDataMap, which writes zones_map_N (the store
       the runtime reads). Without it the backend writes the retired
       `servicezone` collection, which nothing reads any more.
   File = the wide matrix from the ZONE_MAPPING sample: header
   "Pickup/Drop Pincode", one column per drop pincode, zone code in each cell.
   Visual language: supply-side module (SupplyHero, flat cards, FileDrop).
   ========================================================================== */
import React, {useState} from 'react'
import {Box, Button, Stack, Typography} from '@mui/material'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined'
import {apiConstants, postMultipart} from '../../common/constants'
import PageTitle from '../../components/PageTitle'
import DownloadSampleFile from '../../components/DownloadSampleFile'
import {Badge, ConfirmDialog, FileDrop, SupplyHero, useToast} from '../supply-side/components'
import {MERCHANT_ZONE_CP_ID} from './zoneMeta'
import {BTN_SX, Eyebrow, MONO, Note, PAGE_MAX, ResponsePanel, SectionLabel, ZoneCard, apiMessage} from './zoneUi'

const STEPS = [
  ['1', 'Download the sample', 'Start from the ZONE_MAPPING template so the header and layout are right.'],
  ['2', 'Fill the matrix', 'One row per pickup pincode, one column per drop pincode, zone code 1–5 in each cell.'],
  ['3', 'Upload', 'Rows are upserted per lane. Lanes not in the file stay as they are.'],
]

export default function MerchantZoneBaseUpload() {
  const toast = useToast()
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [lastMessage, setLastMessage] = useState(null)
  const [lastOk, setLastOk] = useState(null)

  const upload = async () => {
    setConfirm(false)
    if (!file) return toast('Drop the zone matrix CSV first.', 'warning')
    setBusy(true)
    setLastMessage(null)
    const form = new FormData()
    form.append('file', file)
    form.append('type', 'BULK_SLA')
    form.append('prozoId', MERCHANT_ZONE_CP_ID)
    form.append('alt', 'true')
    try {
      const res = await postMultipart(apiConstants.SERVICEABILITY_UPLOAD, form)
      const msg = typeof res?.data === 'string' ? res.data : JSON.stringify(res?.data)
      const ok = /successful\.?$/i.test(String(msg || '').trim()) && !/not successful/i.test(String(msg || ''))
      setLastMessage(msg)
      setLastOk(ok)
      toast(ok ? 'Base zone map uploaded.' : msg || 'Upload finished with errors', ok ? 'success' : 'error')
      if (ok) setFile(null)
    } catch (err) {
      const msg = apiMessage(err)
      setLastMessage(msg)
      setLastOk(false)
      toast(msg, 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageTitle>Base Zone Upload</PageTitle>
      <SupplyHero
        icon={<GridOnOutlinedIcon />}
        eyebrow={<Eyebrow>Merchant Setup · Zone Mapping</Eyebrow>}
        title='Base Zone Upload'
        subtitle='The platform-wide pickup → drop zone matrix every merchant prices on, before any per-merchant override.'
        sx={{maxWidth: PAGE_MAX}}
      />

      <ZoneCard title='How it works' description='Three steps; the whole file is one matrix.'>
        <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5}>
          {STEPS.map(([n, t, d]) => (
            <Box key={n} sx={{flex: 1, p: 1.75, borderRadius: 2, border: 1, borderColor: 'divider'}}>
              <Stack direction='row' spacing={1.25} alignItems='flex-start'>
                <Box sx={{width: 26, height: 26, borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'primary.main', color: '#fff', fontSize: 12.5, fontWeight: 700}}>
                  {n}
                </Box>
                <Box>
                  <Typography sx={{fontSize: 13, fontWeight: 700}}>{t}</Typography>
                  <Typography sx={{fontSize: 12, color: 'text.secondary', mt: 0.25}}>{d}</Typography>
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
        <Note sx={{mt: 2}}>
          First column header must be <Box component='span' sx={{fontFamily: MONO}}>Pickup/Drop Pincode</Box>. Stored under courier id{' '}
          <Box component='span' sx={{fontFamily: MONO}}>{MERCHANT_ZONE_CP_ID}</Box>; courier zone maps are not affected.
        </Note>
      </ZoneCard>

      <ZoneCard
        title='Upload the matrix'
        description='CSV only. Pricing for all merchants on the affected lanes changes once the upload completes.'
        actions={lastOk == null ? null : <Badge status={lastOk ? 'active' : 'failed'} label={lastOk ? 'Last upload OK' : 'Last upload failed'} />}
      >
        <SectionLabel sx={{mb: 0.75}}>Zone matrix file *</SectionLabel>
        <FileDrop accept='.csv' title='Drag & drop the zone matrix CSV here' hint='CSV from the ZONE_MAPPING sample' maxMB={50}
          onFile={(f) => setFile(f)} onError={(m) => toast(m, 'error')} />

        <Stack direction='row' spacing={1.5} alignItems='center' flexWrap='wrap' useFlexGap sx={{mt: 2}}>
          <Button variant='contained' disableElevation size='small' startIcon={<CloudUploadOutlinedIcon />} onClick={() => setConfirm(true)} disabled={busy || !file}
            sx={BTN_SX}>
            {busy ? 'Uploading…' : 'Upload base zone map'}
          </Button>
          <DownloadSampleFile fileName='ZONE_MAPPING' />
        </Stack>

        <Note tone='warning' sx={{mt: 2}}>
          Lane lookups are cached for up to 3 hours per lane, so a changed zone can take that long to show for lanes priced just before the
          upload. There is no undo: re-upload the previous matrix to roll back.
        </Note>

        <ResponsePanel title='Server response' value={lastMessage} />
      </ZoneCard>

      <ConfirmDialog
        open={confirm}
        title='Upload the base zone map?'
        body={'This upserts every lane in ' + (file?.name || 'the file') + ' into the merchant base zone map and affects pricing for all merchants on those lanes. Lanes not in the file are untouched.'}
        okLabel='Upload'
        onOk={upload}
        onClose={() => setConfirm(false)}
      />
    </>
  )
}
